import React, { createContext, ComponentType, FC } from 'react';

import { useCurrentUser, useProtectedClient } from 'hooks';
import { Feature } from 'api';

export interface IWithFeatures {
  features: {
    leaderboard: boolean;
    comments: boolean;
    legacyAuthDomain: boolean;
    privateGroups: boolean;
    skipOnboardingSelectGroups: boolean;
    dashboardFilters: boolean;
    moderatorDashboard: boolean;
    msteams: boolean;
    nativePostV2: boolean;
    mobile_force_edge: boolean;
    slack: boolean;
    editor_v2: boolean;
    webNotifications: boolean;
  };
}

const FeaturesContext = createContext<IWithFeatures>({
  features: {
    leaderboard: false,
    comments: false,
    legacyAuthDomain: false,
    privateGroups: false,
    skipOnboardingSelectGroups: false,
    dashboardFilters: false,
    moderatorDashboard: false,
    msteams: false,
    nativePostV2: false,
    mobile_force_edge: false,
    slack: false,
    editor_v2: false,
    webNotifications: false,
  },
});

const FeaturesProvider: FC = ({ children }) => {
  const client = useProtectedClient();
  const { leaderboard_enabled } = useCurrentUser();

  const features = client.features || [];
  const clientFeatures: IWithFeatures['features'] = {
    leaderboard: leaderboard_enabled,
    comments: !client.disable_comments,
    legacyAuthDomain: features.includes(Feature.LegacyAuthDomain),
    privateGroups: features.includes(Feature.PrivateGroups),
    skipOnboardingSelectGroups: features.includes(Feature.SkipOnboardingSelectGroups),
    dashboardFilters: features.includes(Feature.DashboardFilters),
    moderatorDashboard: features.includes(Feature.ModeratorDashboard),
    msteams: features.includes(Feature.MsTeams),
    nativePostV2: features.includes(Feature.NativePostV2),
    mobile_force_edge: features.includes(Feature.MobileForceEdge),
    slack: features.includes(Feature.Slack),
    editor_v2: features.includes(Feature.EditorV2),
    webNotifications: features.includes(Feature.WebNotifications),
  };

  return (
    <FeaturesContext.Provider value={{ features: clientFeatures }}>
      {children}
    </FeaturesContext.Provider>
  );
};

const withFeatures =
  <T extends IWithFeatures>(Component: ComponentType<T>) =>
  (props: T) =>
    (
      <FeaturesContext.Consumer>
        {(providerProps: IWithFeatures) => <Component {...props} {...providerProps} />}
      </FeaturesContext.Consumer>
    );

export { FeaturesContext, FeaturesProvider, withFeatures };
